import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import type { TimeRange } from '../components/charts/PriceChart';
import type { TiingoNewsArticle } from './newsTypes';

// Ordered by editorial reliability (index = rank, lower = better)
const SOURCE_RANKING = [
    'bloomberg.com',
    'reuters.com',
    'cnbc.com',
    'forbes.com',
    'finance.yahoo.com',
    'bitcoinmagazine.com',
    'fool.com',
    'blockonomi.com',
    'zacks.com',
];
const SOURCE_RANK = new Map(SOURCE_RANKING.map((s, i) => [s, i]));

const TIER1_SOURCES = new Set([
    'bloomberg.com',
    'reuters.com',
    'cnbc.com',
    'forbes.com',
    'finance.yahoo.com',
    'bitcoinmagazine.com',
]);

const TIER1_THRESHOLD = 10;
const MAX_DISPLAY = 50;

const MAX_CACHE = 50;
const cache = new Map<string, TiingoNewsArticle[]>();

function cacheGet(key: string): TiingoNewsArticle[] | undefined {
    const val = cache.get(key);
    if (val !== undefined) {
        cache.delete(key);
        cache.set(key, val);
    }
    return val;
}

function cacheSet(key: string, val: TiingoNewsArticle[]): void {
    if (cache.size >= MAX_CACHE) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
    }
    cache.set(key, val);
}

export async function fetchAssetNews(
    ticker: string,
    timeRange: TimeRange,
    signal?: AbortSignal,
): Promise<TiingoNewsArticle[]> {
    const key = `${ticker.toLowerCase()}:${timeRange}`;
    const cached = cacheGet(key);
    if (cached) return cached;

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    try {
        const functions = firebase.app().functions('europe-west6');
        const callable = functions.httpsCallable('loadNews');
        const result = await callable({ ticker, timeRange });

        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

        const json = result.data;
        if (!Array.isArray(json)) {
            console.warn('loadNews: unexpected response shape', typeof json);
            return [];
        }
        let articles: TiingoNewsArticle[] = (json as TiingoNewsArticle[])
            .sort((a, b) => {
                const rankA = SOURCE_RANK.get(a.source.toLowerCase()) ?? SOURCE_RANKING.length;
                const rankB = SOURCE_RANK.get(b.source.toLowerCase()) ?? SOURCE_RANKING.length;
                if (rankA !== rankB) return rankA - rankB;
                return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
            });
        if (articles.length > TIER1_THRESHOLD) {
            const tier1Only = articles.filter(a => TIER1_SOURCES.has(a.source.toLowerCase()));
            if (tier1Only.length > 0) articles = tier1Only;
        }
        articles = articles
            .slice(0, MAX_DISPLAY)
            .sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime());
        cacheSet(key, articles);
        return articles;
    } catch (err) {
        if ((err as Error).name !== 'AbortError') {
            console.warn('loadNews fetch failed:', err);
        }
        return [];
    }
}
